import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../axiosInstance";

export const getCustomerDetails = createAsyncThunk(
  "customer/getCustomerDetails",
  async ({ token, deviceType = 2 }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/customer/${token}`, {
        headers: {
          "Device-Type": deviceType,
        },
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message || "Something went wrong",
      );
    }
  },
);

export const getCustomerMealPlans = createAsyncThunk(
  "customer/getCustomerMealPlans",
  async ({ token, deviceType = 2 }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/customer/${token}/meal-plans`, {
        headers: {
          "Device-Type": deviceType,
        },
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message || "Something went wrong",
      );
    }
  },
);

export const getMealPlanDetail = createAsyncThunk(
  "customer/getMealPlanDetail",
  async ({ token, mealPlanId, deviceType = 2 }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/customer/${token}/meal-plans/${mealPlanId}`, {
        headers: {
          "Device-Type": deviceType,
        },
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message || "Something went wrong",
      );
    }
  },
);

export const getCustomerOrders = createAsyncThunk(
  "customer/getCustomerOrders",
  async ({ token, page = 1, perPage = 100, status, filter, orderCategory, mealTiming, deliveryType, cancelledBy, startDate, endDate, search, deviceType = 2 }, { rejectWithValue }) => {
    try {
      const params = {};
      if (page) params.page = page;
      if (perPage) params.per_page = perPage;
      if (status && status !== "--" && status !== "") params.status = status;
      if (filter && filter !== "--" && filter !== "") params.filter = filter;
      if (search && search !== "--" && search !== "") params.search = search;
      if (orderCategory && orderCategory !== "--" && orderCategory !== "") params.order_category = orderCategory;
      if (mealTiming && mealTiming !== "--" && mealTiming !== "") params.meal_timing = mealTiming;
      if (deliveryType && deliveryType !== "--" && deliveryType !== "") params.delivery_type = deliveryType;
      if (cancelledBy && cancelledBy !== "--" && cancelledBy !== "") params.cancelled_by = cancelledBy;
      if (startDate && startDate !== "--" && startDate !== "") params.start_date = startDate;
      if (endDate && endDate !== "--" && endDate !== "") params.end_date = endDate;

      const res = await axiosInstance.get(`/customer/${token}/orders`, {
        params,
        headers: {
          "Device-Type": deviceType,
        },
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message || "Something went wrong",
      );
    }
  },
);

export const getOrderDetail = createAsyncThunk(
  "customer/getOrderDetail",
  async ({ token, orderId, deviceType = 2 }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/customer/${token}/orders/${orderId}`, {
        headers: {
          "Device-Type": deviceType,
        },
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message || "Something went wrong",
      );
    }
  },
);

export const getCustomerBills = createAsyncThunk(
  "customer/getCustomerBills",
  async ({ token, deviceType = 2 }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/customer/${token}/bills`, {
        headers: {
          "Device-Type": deviceType,
        },
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message || "Something went wrong",
      );
    }
  },
);

export const getBillDetail = createAsyncThunk(
  "customer/getBillDetail",
  async ({ token, billId, deviceType = 2 }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/customer/${token}/bills/${billId}`, {
        headers: {
          "Device-Type": deviceType,
        },
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message || "Something went wrong",
      );
    }
  },
);

export const downloadOrderBillPdf = createAsyncThunk(
  "customer/downloadOrderBillPdf",
  async ({ token, billId, format = "standard", stream = 0, deviceType = 2 }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(
        `/customer/${token}/download-order-bill-pdf`,
        {
          bill_id: String(billId),
          format: format,
          stream: stream,
        },
        {
          headers: {
            "Device-Type": deviceType,
          },
        }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message || "Something went wrong"
      );
    }
  }
);

export const cancelCustomerOrder = createAsyncThunk(
  "customer/cancelCustomerOrder",
  async ({ token, orderId, isConfirmed = 1, cancellationReason = "Changed my mind", deviceType = 2 }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(
        `/customer/${token}/cancel-order`,
        {
          order_id: String(orderId),
          is_confirmed: isConfirmed,
          cancellation_reason: cancellationReason,
        },
        {
          headers: {
            "Device-Type": deviceType,
          },
        }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message || "Something went wrong"
      );
    }
  }
);

export const getCustomerVerification = createAsyncThunk(
  "customer/getCustomerVerification",
  async ({ token, deviceType = 2 }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/customer/${token}/verify`, {
        headers: {
          "Device-Type": deviceType,
        },
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message || "Something went wrong",
      );
    }
  },
);

export const sendVerificationOtp = createAsyncThunk(
  "customer/sendVerificationOtp",
  async ({ token, email, deviceType = 2 }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(
        `/customer/${token}/send-verification-otp`,
        { email },
        {
          headers: {
            "Device-Type": deviceType,
          },
        }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message || "Something went wrong"
      );
    }
  }
);

export const verifyOtp = createAsyncThunk(
  "customer/verifyOtp",
  async ({ token, otp, deviceType = 2 }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(
        `/customer/${token}/verify-otp`,
        { otp },
        {
          headers: {
            "Device-Type": deviceType,
          },
        }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || err.message || "Something went wrong"
      );
    }
  }
);
